import type { FieldDef, ProjectFieldRef, SchemaIndex } from "@gjwtrack/query-language";
import { useState, type ReactNode } from "react";
import { fieldValueText } from "../lib/format.ts";
import { FieldEditor, isPickerKind, pickerOptions } from "./FieldEditor.tsx";
import { ValueSelect } from "../ui/ValueSelect.tsx";

type Values = ReadonlyArray<number | string>;

/** One row of the YouTrack-style field panel: label over value, badge on the right. Issue page and New issue dialog share it. */
export function FieldRow({ label, required, badge, children }: { label: string; required?: boolean; badge?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-muted">
          {label}
          {required && <span className="text-danger"> *</span>}
        </div>
        <div className="text-[14.5px]">{children}</div>
      </div>
      {badge && <div className="shrink-0">{badge}</div>}
    </div>
  );
}

/** Value shown as link-blue text. Option sets and users open the picker; typed fields open an inline editor. */
export function PanelValue({ schema, def, pf, values, editable, onChange }: { schema: SchemaIndex; def: FieldDef; pf: ProjectFieldRef | undefined; values: Values; editable: boolean; onChange: (v: Values) => void }) {
  const [open, setOpen] = useState(false);
  // The project's own "Empty Value" text (project settings → Custom Fields), else YouTrack's default.
  const empty = pf?.emptyText ?? `No ${(pf?.alias ?? def.name).toLowerCase()}`;
  const text = values.length ? values.map((v) => fieldValueText(schema, def, v)).join(", ") : empty;
  if (isPickerKind(def)) {
    // Ring's inline Select: link-blue value, click opens the filterable list — YouTrack's field panel.
    return (
      <ValueSelect
        inline
        options={pickerOptions(schema, def, pf)}
        selected={new Set(values.filter((v): v is number => typeof v === "number"))}
        multi={def.multi}
        clearable={!(pf?.required ?? false)}
        clearLabel={empty}
        emptyLabel={empty}
        disabled={!editable}
        onChange={onChange}
      />
    );
  }
  const trigger = (
    <button type="button" className={`text-left ${values.length ? "text-value" : "text-value/80"} ${editable ? "hover:underline" : "cursor-default"}`} onClick={() => editable && setOpen(true)} disabled={!editable}>
      {text}
    </button>
  );
  if (open) {
    return (
      <div onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setOpen(false)}>
        <FieldEditor
          schema={schema}
          def={def}
          pf={pf}
          values={values}
          onChange={(v) => {
            onChange(v);
            setOpen(false);
          }}
        />
      </div>
    );
  }
  return trigger;
}
